import React from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Share2, User, Calendar, TrendingUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface Idea {
  id: string;
  title: string;
  description: string | null;
  tagline: string | null;
  niche: string;
  problem: string;
  solution: string;
  tags: string[];
  score: number;
  created_at: string;
  user_id: string;
}

interface Profile {
  username?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  full_name?: string | null;
}

interface ActionPlan {
  day_number: number;
  task: string;
  status: string;
}

const PublicIdea = () => {
  const { slug } = useParams<{ slug: string }>();
  const [idea, setIdea] = useState<Idea | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [actionPlans, setActionPlans] = useState<ActionPlan[]>([]);
  const [progressCount, setProgressCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      fetchPublicIdea();
    }
  }, [slug]);

  useEffect(() => {
    if (idea) {
      document.title = `${idea.title} | DayOne`;
      
      // Set meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      const description = idea.tagline || idea.description || idea.problem;
      const truncatedDesc = description?.substring(0, 160) || "Startup idea showcase";
      
      if (metaDescription) {
        metaDescription.setAttribute("content", truncatedDesc);
      } else {
        const meta = document.createElement("meta");
        meta.name = "description";
        meta.content = truncatedDesc;
        document.head.appendChild(meta);
      }
    }

    return () => {
      document.title = "DayOne";
    };
  }, [idea]);

  const fetchPublicIdea = async () => {
    try {
      setLoading(true);

      const { data: ideaData, error: ideaError } = await supabase
        .from("ideas")
        .select("*")
        .eq("public_slug", slug)
        .eq("is_public", true)
        .maybeSingle();

      if (ideaError) throw ideaError;
      if (!ideaData) {
        navigate("/404");
        return;
      }

      setIdea(ideaData as unknown as Idea);

      // Fetch creator profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", ideaData.user_id)
        .maybeSingle();

      setProfile(profileData as any);

      // Fetch action plans
      const { data: plansData } = await supabase
        .from("action_plans")
        .select("day_number, task, status")
        .eq("idea_id", ideaData.id)
        .order("day_number", { ascending: true });

      if (plansData) {
        setActionPlans(plansData);
      }

      // Fetch progress stats
      const { count } = await supabase
        .from("progress_logs")
        .select("id", { count: "exact" })
        .eq("idea_id", ideaData.id);

      setProgressCount(count || 0);
    } catch (err) {
      console.error("Error fetching public idea:", err);
      toast({
        title: "Error",
        description: "Failed to load idea",
        variant: "destructive",
      });
      navigate("/404");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied!",
      description: "Share this idea with others",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!idea) return null;

  const groupedPlans = actionPlans.reduce((acc, plan) => {
    if (!acc[plan.day_number]) {
      acc[plan.day_number] = [];
    }
    acc[plan.day_number].push(plan);
    return acc;
  }, {} as Record<number, ActionPlan[]>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6 flex justify-end">
          <Button onClick={handleShare} variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <CardTitle className="text-3xl mb-2">{idea.title}</CardTitle>
                {idea.tagline && (
                  <CardDescription className="text-base">{idea.tagline}</CardDescription>
                )}
              </div>
              <Badge variant="secondary" className="text-lg font-bold">
                {idea.score}
              </Badge>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t">
              <Avatar className="h-10 w-10">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback>
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{(profile as any)?.username || "Anonymous Creator"}</p>
                <p className="text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  {new Date(idea.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {idea.description && (
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-muted-foreground">{idea.description}</p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">Problem</h3>
                <p className="text-muted-foreground text-sm">{idea.problem}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Solution</h3>
                <p className="text-muted-foreground text-sm">{idea.solution}</p>
              </div>
            </div>

            {idea.tags && idea.tags.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {idea.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-6 pt-4 border-t">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">{progressCount}</p>
                  <p className="text-xs text-muted-foreground">Progress Events</p>
                </div>
              </div>
              {actionPlans.length > 0 && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">
                      {actionPlans.filter((p) => p.status === "done").length}/{actionPlans.length}
                    </p>
                    <p className="text-xs text-muted-foreground">Tasks Completed</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {actionPlans.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>7-Day Action Plan</CardTitle>
              <CardDescription>Roadmap for bringing this idea to life</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {Object.entries(groupedPlans).map(([day, tasks]) => {
                  const dayNum = parseInt(day);
                  const completedTasks = tasks.filter((t) => t.status === "done").length;
                  const totalTasks = tasks.length;

                  return (
                    <AccordionItem key={day} value={`day-${day}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-4">
                          <span className="font-semibold">Day {dayNum}</span>
                          <Badge variant={completedTasks === totalTasks ? "default" : "secondary"}>
                            {completedTasks}/{totalTasks} done
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2 ml-4">
                          {tasks.map((task, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <span
                                className={`mt-1 ${
                                  task.status === "done" ? "text-primary" : "text-muted-foreground"
                                }`}
                              >
                                {task.status === "done" ? "✓" : "◦"}
                              </span>
                              <span className={task.status === "done" ? "line-through" : ""}>
                                {task.task}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicIdea;
